/**
 * Lines by name in the players (shared/voicelines.ts, docs/voice.md "Lines by name"): the Preact
 * app's src/audio.ts and the Expo app's client/lib/audio/player.web.ts and player.native.ts. The
 * server makes and keeps each name's clip (server/voicelines.ts); a player fetches it with the
 * device's own name, keeps it (the browser's cache on the web, a file in the cache directory in the
 * apps), and plays it like any line. A clip is in no directory's lines.json or envelopes.json, so
 * what it says and its mouth are remembered here, from its own WAV, for the bubble (speechTextFor)
 * and the mouth (loadEnvelopes) to find under PERSONAL_BASE.
 */
import { computeEnvelopeFromPcm } from "./peonEnvelope.ts";
import { readWav } from "./voice-clip.ts";

/** A line chosen ahead of the player's own pick: its directory and name for the bubble and mouth, and what to load. */
export interface ChosenLine {
  base: string;
  name: string;
  src: string;
}

/**
 * The header the clip's fetch carries: the device's name as every request sends it (the clients'
 * clientHeaders and memberHeaders), which is the name the server voices. The players fetch clips
 * themselves, since the API modules are more than a sound module should load.
 */
export function personalHeaders(name: string): Record<string, string> {
  return { "x-peon-name": encodeURIComponent(name) };
}

const spoken = new Map<string, { text: string; envelope: number[] }>();

/** Remembers what the clip at `url` says, and its mouth from its WAV (none when the bytes are not one). */
export function notePersonalClip(url: string, text: string, wav: Uint8Array): void {
  const decoded = readWav(wav);
  spoken.set(url, { text, envelope: decoded ? computeEnvelopeFromPcm(decoded.samples, decoded.sampleRate) : [] });
}

/** What the clip at `url` says, or null when it has not been fetched here. */
export function personalText(url: string): string | null {
  return spoken.get(url)?.text ?? null;
}

/** Every fetched clip's mouth, keyed by its url, as a directory's envelopes.json is keyed by file. */
export function personalEnvelopes(): Record<string, number[]> {
  const out: Record<string, number[]> = {};
  for (const [url, { envelope }] of spoken) if (envelope.length) out[url] = envelope;
  return out;
}

/** `pending`'s value if it settles within `ms`, else null; a rejection is null too. The work itself carries on. */
export function within<T>(pending: Promise<T>, ms: number): Promise<T | null> {
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(null), ms);
    pending.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      () => {
        clearTimeout(timer);
        resolve(null);
      },
    );
  });
}

/** Forgets every clip (tests). */
export function resetPersonalClipsForTests(): void {
  spoken.clear();
}
